import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Coins } from "lucide-react";
import { api, money } from "@/lib/api";
import { isVerified } from "@/lib/account";
import { useAuth } from "@/context/AuthContext";
import TradeDialog from "./TradeDialog";

export default function InvestPanel({ assetType, asset, price, label, maxBuy, onDone }) {
  const { user, checkAuth } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [mode, setMode] = useState(null);
  const isClient = user?.role === "client";

  const loadPortfolio = () => api.get("/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
  useEffect(() => { if (isClient) loadPortfolio(); }, [isClient]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isClient && !isVerified(user)) checkAuth(); }, [isClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const held = portfolio?.holdings.find((h) => h.asset_type === assetType && h.asset_id === asset.id)?.quantity || 0;
  const accent = assetType === "stock" ? "btn-primary" : "btn-gold";

  return (
    <div className="panel sticky top-24 p-6 sm:p-8" data-testid="invest-panel">
      <p className="eyebrow">Invest</p>
      <p className="mt-3 font-display text-2xl font-semibold text-white">{label}</p>
      <p className="mt-1 font-mono text-slate-400">{money(price)} per share</p>
      {!user && (
        <div className="mt-8 space-y-3">
          <Link to="/login" className={`${accent} w-full`} data-testid="invest-login-button">Log in to invest</Link>
          <Link to="/register" className="btn-ghost w-full" data-testid="invest-register-button">Open an account</Link>
        </div>
      )}
      {user?.role === "admin" && <p className="mt-8 text-sm text-slate-400" data-testid="invest-admin-note">You're signed in as admin. Manage this listing from the admin panel.</p>}
      {isClient && !isVerified(user) && (
        <div className="mt-8 space-y-3" data-testid="invest-verification-required">
          <p className="text-sm text-slate-400">Complete email and identity verification to start investing.</p>
          <Link to="/verification" className={`${accent} w-full`} data-testid="invest-verify-button">Complete verification</Link>
        </div>
      )}
      {isClient && isVerified(user) && (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4 rounded-xl border border-line bg-obsidian/50 p-4 text-sm">
            <div><p className="text-slate-500">Cash available</p><p className="mt-1 font-mono text-white" data-testid="invest-cash-balance">{money(portfolio?.cash_balance)}</p></div>
            <div><p className="text-slate-500">You hold</p><p className="mt-1 font-mono text-white" data-testid="invest-held-qty">{held} shares</p></div>
          </div>
          <div className="mt-6 grid gap-3">
            <button onClick={() => setMode("buy")} disabled={maxBuy === 0} className={`${accent} w-full`} data-testid="invest-buy-button">
              <ShoppingCart className="h-4 w-4" /> {maxBuy === 0 ? "Fully funded" : "Buy shares"}
            </button>
            {held > 0 && <button onClick={() => setMode("sell")} className="btn-ghost w-full" data-testid="invest-sell-button"><Coins className="h-4 w-4" /> Sell shares</button>}
          </div>
          {portfolio && portfolio.cash_balance < price && (
            <p className="mt-4 text-xs text-slate-500">Not enough cash? <Link to="/dashboard" className="text-emerald-400" data-testid="invest-deposit-link">Request a deposit</Link></p>
          )}
          <TradeDialog open={!!mode} onOpenChange={(o) => !o && setMode(null)} mode={mode || "buy"} assetType={assetType} assetId={asset.id}
            label={label} price={price} cash={portfolio?.cash_balance} maxQty={mode === "sell" ? held : maxBuy}
            onDone={() => { loadPortfolio(); onDone?.(); }} />
        </>
      )}
    </div>
  );
}
